<template>
  <NuxtLayout
    name="contentful-author"
    content-wrapper-class="!pt-6"
    :accent-wrapper-class="{
      'pb-[5.25rem]': !_isEmpty(author.knownFor)
    }"
  >
    <template #header>
      <V2MoleculesBreadCrumb
        class="!text-blue-50 w-full max-w-wrapper-lg mx-auto"
        caret-class="!mx-1"
        :pages="breadcrumbs"
      />

      <V2MoleculesAuthorProfileCard
        class="w-full max-w-wrapper-lg mx-auto"
        :author="author"
      />
    </template>

    <V2MoleculesAuthorFeaturePanel
      v-if="!_isEmpty(author.knownFor)"
      class="flex-col md:flex-wrap !items-start md:items-center gap-4 rounded-2xl -mt-[5.25rem]"
    >
      <template #label>
        Known for
      </template>

      <div class="flex flex-1 flex-wrap w-full max-w-full gap-2">
        <V2AtomsTag
          v-for="(item, itemIndex) in author.knownFor"
          :key="`known-for-${itemIndex}`"
          color="whiteb"
          class="py-1 px-3 shrink-0"
          :class="{
            'mr-4 md:mr-0': itemIndex === author.knownFor.length - 1
          }"
        >
          {{ item }}
        </V2AtomsTag>
      </div>
    </V2MoleculesAuthorFeaturePanel>

    <div
      v-if="hasAuthorDetails"
      class="grid md:grid-cols-2 gap-4 md:gap-y-10 md:gap-x-8"
    >
      <div
        v-if="!_isEmpty(author.expertise)"
        :class="component.grid.item"
      >
        <h2 :class="component.grid.title">
          Expertise
        </h2>

        <RichTextRenderer
          :document="author.expertise.json"
        />
      </div>

      <div
        v-if="!_isEmpty(author.experience)"
        :class="component.grid.item"
      >
        <h2 :class="component.grid.title">
          Experience
        </h2>

        <RichTextRenderer
          :document="author.experience.json"
        />
      </div>

      <div
        v-if="!_isEmpty(author.education)"
        :class="component.grid.item"
      >
        <h2 :class="component.grid.title">
          Education
        </h2>

        <RichTextRenderer
          :document="author.education.json"
        />
      </div>

      <div
        v-if="!_isEmpty(publications)"
        :class="component.grid.item"
        class="md:col-span-2 md:flex-row md:gap-x-8"
      >
        <div class="w-full max-w-[328px] flex flex-col gap-2">
          <h2 :class="component.grid.title">
            Featured publications
          </h2>

          <p>
            {{ authorFirstName }}'s commentary has appread in:
          </p>
        </div>

        <ul class="!grid grid-flow-col grid-rows-5 gap-x-8">
          <li
            v-for="(item, itemIndex) in publications"
            :key="`publication-${itemIndex}`"
            class="font-bold"
          >
            <NuxtLink
              v-if="item.url"
              :to="item.url"
              class="underline"
              target="_blank"
            >
              {{ item.name }}
            </NuxtLink>

            <template v-else>
              {{ item.name }}
            </template>
          </li>
        </ul>
      </div>

      <div
        v-if="!_isEmpty(videos)"
        :class="component.grid.item"
        class="md:col-span-2 md:flex-row md:gap-x-8"
      >
        <div class="w-full max-w-[328px] flex flex-col gap-2">
          <h2 :class="component.grid.title">
            Featured videos
          </h2>
        </div>

        <div class="flex flex-col md:grid md:grid-cols-2 flex-1 gap-4 md:gap-6">
          <div
            v-for="(item, itemIndex) in videos"
            :key="`video-${itemIndex}`"
            class="font-bold flex flex-col gap-4 w-full cursor-pointer hover:opacity-95 transition-all"
            @click="openVideoModal(item)"
          >
            <div class="relative w-full h-40">
              <video
                :src="item.url"
                class="h-40 w-full object-cover object-top rounded-[0.625rem] pointer-events-none"
                loading="lazy"
              />

              <img
                src="/icons/play_video.png"
                alt="Play video"
                class="absolute inset-0 m-auto h-12 w-12 pointer-events-none"
              >
            </div>

            <p class="text-xl font-bold">
              {{ item.title }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-10 mb-4">
      <V2MoleculesPostTileGroup
        v-if="!_isEmpty(mediaPosts?.items)"
        class="w-full max-w-wrapper-lg mx-auto mt-[1.125rem]"
        title="Media"
        title-class="mb-6"
        badge-theme="outline"
        prefix-author-url="/our-experts"
        :items="mediaPosts?.items"
        :items-per-row="3"
        :initial-items-count="3"
        :has-load-more="pageConfig.media.limit < pageConfig.media.total"
        :show-author="false"
        :see-all-link="`/media/`"
        show-see-all
        @load-more="handleLoadMore('media')"
      />

      <V2MoleculesPostTileGroup
        v-if="!_isEmpty(expertAnalysisPosts?.items)"
        class="w-full max-w-wrapper-lg mx-auto mt-[1.125rem]"
        title="Expert Analysis"
        title-class="mb-6"
        badge-theme="outline"
        prefix-author-url="/our-experts"
        :items="expertAnalysisPosts?.items"
        :items-per-row="3"
        :initial-items-count="3"
        :has-load-more="pageConfig.expertAnalysis.limit < pageConfig.expertAnalysis.total"
        :show-author="false"
        :see-all-link="`/expert-analysis/`"
        show-see-all
        @load-more="handleLoadMore('expertAnalysis')"
      />

      <V2MoleculesPostTileGroup
        v-if="!_isEmpty(guidesPosts?.items)"
        class="w-full max-w-wrapper-lg mx-auto mt-[1.125rem]"
        title="Guides"
        title-class="mb-6"
        badge-theme="outline"
        prefix-author-url="/our-experts"
        :items="guidesPosts?.items"
        :items-per-row="3"
        :initial-items-count="3"
        :has-load-more="pageConfig.guides.limit < pageConfig.guides.total"
        :show-author="false"
        :see-all-link="`/health-insurance/guides/`"
        show-see-all
        @load-more="handleLoadMore('guides')"
      />
    </div>

    <V2MoleculesEditorialTrustCard class="w-full max-w-wrapper-lg mx-auto" />

    <V2MoleculesModal
      ref="refVideoModal"
      :cta-buttons="[]"
      card-class="w-full !max-w-[744px] !p-0 !border-0 !rounded-none !bg-transparent"
      close-icon-color="surface.default"
      close-reserve-class="!pt-12"
      close-icon-class="!right-0"
      :show-asset="false"
      @toggle="onVideoModalToggle"
    >
      <template #body>
        <video
          v-if="selectedVideo"
          :src="selectedVideo.url"
          class="w-full max-h-[70vh] bg-white"
          loading="lazy"
          controls
          autoplay
        />
      </template>
    </V2MoleculesModal>
  </NuxtLayout>
</template>

<script setup>
import RichTextRenderer from 'contentful-rich-text-vue-renderer'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import { storeToRefs } from 'pinia'

import { MEDIA_LOAD_MORE_PER_PAGE } from '~/constants/pages/contentful'

defineOptions({
  name: 'AuthorsPage'
})

const $route = useRoute()

const authorStore = useAuthorStore()
const { author, videos, publications } = storeToRefs(authorStore)

const mediaStore = useMediaStore()
const { authorPosts: mediaPosts } = storeToRefs(mediaStore)

const expertAnalysisStore = useExpertAnalysisStore()
const { authorPosts: expertAnalysisPosts } = storeToRefs(expertAnalysisStore)

const guidesStore = useGuidesStore()
const { authorPosts: guidesPosts } = storeToRefs(guidesStore)

const POST_TYPE_STORE_MAP = {
  media: mediaStore,
  expertAnalysis: expertAnalysisStore,
  guides: guidesStore
}

const AUTHOR = $route.params.author

const pageConfig = ref({
  media: { limit: MEDIA_LOAD_MORE_PER_PAGE, total: undefined },
  expertAnalysis: { limit: MEDIA_LOAD_MORE_PER_PAGE, total: undefined },
  guides: { limit: MEDIA_LOAD_MORE_PER_PAGE, total: undefined }
})

const refVideoModal = ref()
const selectedVideo = ref(null)

await useAsyncData(`author-${AUTHOR}`, async () => {
  await Promise.all([
    authorStore.getAuthor(AUTHOR),
    mediaStore.getAuthorPosts({
      authorUrl: AUTHOR,
      limit: pageConfig.value.media.limit
    }),
    expertAnalysisStore.getAuthorPosts({
      authorUrl: AUTHOR,
      limit: pageConfig.value.expertAnalysis.limit
    }),
    guidesStore.getAuthorPosts({
      authorUrl: AUTHOR,
      limit: pageConfig.value.guides.limit
    })
  ]).then(res => {
    pageConfig.value.media.total = res?.[1]?.total
    pageConfig.value.expertAnalysis.total = res?.[2]?.total
    pageConfig.value.guides.total = res?.[3]?.total
  })

  return true
})

const breadcrumbs = computed(() => [
  {
    text: 'Home',
    name: 'Home',
    url: '/'
  },
  {
    text: 'Our Experts',
    name: 'Our Experts',
    url: '/our-experts/'
  },
  {
    text: author.value.name,
    name: author.value.name,
    url: `/our-experts${author.value.url}/`,
    activeClass: '!text-white',
    active: true
  }
])

const component = computed(() => {
  return {
    grid: {
      title: 'text-semantic-sizing-headline-sm font-semibold',
      item: (() => {
        const base = 'bg-semantic-surface-default rounded-2xl md:rounded-3xl shadow-combined-elevation-1 p-4 md:p-6 text-semantic-sizing-body-sm flex flex-col gap-2 md:gap-4'

        const list = '[&_ul]:flex [&_ul]:flex-col [&_ul]:gap-4 [&_ul]:list-none [&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:before:content-[\'\'] [&_li]:before:inline-block [&_li]:before:shrink-0 [&_li]:before:wh-6 [&_li]:before:bg-[url(/icons/check-circle-purple.png)] [&_li]:before:bg-no-repeat [&_li]:before:bg-contain'

        return `${base} ${list}`
      })()
    }
  }
})

const hasAuthorDetails = computed(() => (
  !_isEmpty(author.value.expertise) ||
  !_isEmpty(author.value.experience) ||
  !_isEmpty(author.value.education) ||
  !_isEmpty(publications.value) ||
  !_isEmpty(videos.value)
))

const postsList = computed(() => [
  ...mediaPosts.value?.items ?? [],
  ...expertAnalysisPosts.value?.items ?? [],
  ...guidesPosts.value?.items ?? []
].filter(e => e))

const authorFirstName = computed(() => {
  const name = author.value?.name.split(' ')

  return name?.[0] ?? name
})

function openVideoModal (item) {
  selectedVideo.value = item
  refVideoModal.value?.toggle(true)
}

function onVideoModalToggle (show) {
  if (!show?.value) {
    selectedVideo.value = null
  }
}

async function handleLoadMore (type) {
  const store = POST_TYPE_STORE_MAP[type]
  const offset = pageConfig.value[type].limit
  const total = pageConfig.value[type].total

  await store?.getAuthorPosts({
    authorUrl: AUTHOR,
    offset,
    limit: MEDIA_LOAD_MORE_PER_PAGE
  }).then(() => {
    const newOffset = offset + MEDIA_LOAD_MORE_PER_PAGE

    pageConfig.value[type].limit = newOffset >= total
      ? total
      : newOffset
  })
}

const PAGE_DETAILS = {
  title: `Our Experts - ${author.value?.name}`,
  description: author.value?.profile,
  ogImage: author.value?.image
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.title,
  description: PAGE_DETAILS.description,
  ogImage: PAGE_DETAILS.ogImage
})

useJsonld(() => {
  const _path = trailingSlash($route.path)
  const routeSlug = $route.params?.author

  const authorName = author.value?.name

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_LOGO_SCHEMA({
        articleUrl: `${trailingSlash(`${CC_DOMAIN}${_path}`)}#logo`,
        imageUrl: PAGE_DETAILS.ogImage || FEATURED_IMAGE_DEFAULT_IMAGE,
        caption: authorName,
        width: 400,
        height: 400
      }),
      GENERATE_WEBPAGE_SCHEMA({
        type: ['WebPage', 'ProfilePage'],
        page: _path,
        pageTitle: PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.description,
        aboutPath: `/#person-${routeSlug}`,
        mainEntityPath: `/#person-${routeSlug}`,
        primaryImageOfPagePath: `${_path}#image`,
        breadcrumbPath: `${_path}#breadcrumb`
      }),
      GENERATE_PERSON_SCHEMA({
        page: _path,
        author: authorName,
        jobTitle: author.value?.jobTitle ?? '',
        description: author.value?.profile ?? '',
        knowsAbout: author.value?.knownFor ?? [],
        isAuthorPage: true
      }),
      GENERATE_ITEM_LIST_SCHEMA({
        page: _path,
        author: authorName,
        list: postsList.value
          .filter(e => e)
          .map((item, index) => ({
            position: index + 1,
            name: item?.title,
            url: trailingSlash(`${CC_DOMAIN}${item?.url}`)
          })),
        totalItems: author.value?.totalPosts ?? 0
      }),
      GENERATE_BREADCRUMB_SCHEMA(breadcrumbs.value)
    ].filter(e => e)
  }
})
</script>
